import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import { isChangeManager } from '../changePermissions';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeExtraFields,
  ChangeExtraValue,
  ChangeFormField,
  ChangeRequest,
  ChangeType,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  buildExtraFieldsPayload,
  hydrateExtraFields,
  validateExtraFields,
  validateNativeRequired,
} from '../changeFormSchema';
import {
  buildChangeFormPayload,
  validateChangeForm,
  type ChangeFormValues,
} from '../changeValidation';
import { validateImmediateManagerPickForSubmit } from '../changeImmediateManagerValidation';
import { toLocalDateInput, toLocalDateTimeInput } from '../goLiveWindow';
import { useChangeCatalogs } from '../hooks/useChangeCatalogs';
import { useChangeTypePolicies } from '../hooks/useChangeTypePolicies';
import ChangeConfigurableForm from './ChangeConfigurableForm';
import ChangeFormFields from './ChangeFormFields';

interface Props {
  change: ChangeRequest;
  onUpdated: () => Promise<void> | void;
}

const formFromChange = (change: ChangeRequest): ChangeFormValues => ({
  title: change.title,
  description: change.description,
  scope: change.scope || '',
  impacted_users: change.impacted_users,
  go_live_start_at: toLocalDateTimeInput(change.go_live_start_at),
  go_live_end_at: toLocalDateTimeInput(change.go_live_end_at),
  risk_committee_start_at: toLocalDateInput(change.risk_committee_start_at),
  risk_committee_end_at: toLocalDateInput(change.risk_committee_end_at),
  camud_go_no_go_date: toLocalDateInput(change.camud_go_no_go_date),
  camud_extraordinary: Boolean(change.camud_extraordinary),
  rollback_plan: change.rollback_plan,
  change_type: change.change_type as ChangeType,
  requester_area_id: change.requester_area_id,
  requester_id: change.requester_id || 0,
  requester_manager_group_id: change.requester_manager_group_id || 0,
  project: change.project,
  category_id: change.category_id,
  sap_classification:
    change.sap_classification === 'sap' || change.sap_classification === 'nao_sap'
      ? change.sap_classification
      : '',
  sap_has_request:
    change.sap_classification === 'sap' && typeof change.sap_has_request === 'boolean'
      ? change.sap_has_request
      : null,
  task_type_ids: Array.from(
    new Set(
      (change.subtasks || [])
        .map((item) => item.task_type_id)
        .filter((value): value is number => Boolean(value)),
    ),
  ),
});

const ChangeEditForm: React.FC<Props> = ({ change, onUpdated }) => {
  const { user } = useAuth();
  const isAdmin = isChangeManager(user);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forceOutside, setForceOutside] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const [form, setForm] = useState<ChangeFormValues>(() => formFromChange(change));
  const [schema, setSchema] = useState<ChangeFormField[]>([]);
  const [extras, setExtras] = useState<ChangeExtraFields>({});
  const canEdit = Boolean(change.can_edit_form);
  const { categories, requesterAreas, taskTypes } = useChangeCatalogs({ enabled: editing && canEdit });
  const { typeOptions } = useChangeTypePolicies({ enabled: editing && canEdit });
  const editFields = useMemo(() => {
    if (schema.some((field) => field.native_key === 'sap_classification')) return schema;
    const sapSlot: ChangeFormField = {
      id: -72,
      kind: 'native',
      native_key: 'sap_classification',
      label: 'SAP ou Não SAP',
      field_type: 'list',
      is_required: true,
      options: [],
      sort_order: 72,
      col_span: 6,
      is_visible: true,
      is_locked: true,
    };
    const afterArea = schema.findIndex((field) => field.native_key === 'requester_area_id');
    if (afterArea < 0) return [...schema, sapSlot];
    return [...schema.slice(0, afterArea + 1), sapSlot, ...schema.slice(afterArea + 1)];
  }, [schema]);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    void changesApi.getFormSchema().then((data) => {
      if (cancelled) return;
      const items = data.items || [];
      setSchema(items);
      setExtras(hydrateExtraFields(items, change.extra_fields));
    });
    return () => {
      cancelled = true;
    };
  }, [editing, change.extra_fields]);

  useEffect(() => {
    if (editing) return;
    setForm(formFromChange(change));
    setForceOutside(false);
    setForceReason('');
    setExtras(hydrateExtraFields(schema, change.extra_fields));
  }, [change, editing, schema]);

  const handleFormChange = useCallback((patch: Partial<ChangeFormValues>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleOverrideChange = useCallback(
    (patch: Partial<{ forceOutside: boolean; forceReason: string }>) => {
      if (patch.forceOutside !== undefined) setForceOutside(patch.forceOutside);
      if (patch.forceReason !== undefined) setForceReason(patch.forceReason);
    },
    [],
  );

  const handleSave = async () => {
    setError(null);
    const override = { isAdmin, forceOutside, forceReason };
    const validationError = validateChangeForm(form, override, editFields, extras);
    if (validationError) {
      setError(validationError);
      return;
    }
    const immediateManagerError = await validateImmediateManagerPickForSubmit(
      form,
      editFields,
      extras,
      change.requester_id,
    );
    if (immediateManagerError) {
      setError(immediateManagerError);
      return;
    }
    const nativeError = validateNativeRequired(
      editFields.map((field) =>
        field.native_key === 'task_type_ids' ||
        field.native_key === 'change_owner_id' ||
        field.native_key === 'requester_id'
          ? { ...field, is_required: false }
          : field,
      ),
      form,
      extras,
    );
    if (nativeError) {
      setError(nativeError);
      return;
    }
    const extraError = validateExtraFields(editFields, form, extras);
    if (extraError) {
      setError(extraError);
      return;
    }

    setSaving(true);
    try {
      await changesApi.update(change.id, {
        ...buildChangeFormPayload(form, override, editFields, extras),
        extra_fields: buildExtraFieldsPayload(schema, form, extras),
      });
      setEditing(false);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a mudança.'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <strong>Dados da mudança</strong>
            <div className="small text-muted">
              {change.category?.name || 'Sem categoria'} · {change.requester_area?.name || 'Sem área'}
            </div>
          </div>
          {canEdit ? (
            <Button size="sm" variant="outline-primary" onClick={() => setEditing(true)}>
              Editar dados
            </Button>
          ) : (
            <span className="small text-muted">
              Edição indisponível neste status ou para o seu perfil.
            </span>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 className="mb-0">Editar mudança</h5>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={saving}
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
            >
              Cancelar
            </Button>
            <Button size="sm" disabled={saving} onClick={() => void handleSave()}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>

        {error ? (
          <Alert variant="danger" className="py-2" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {schema.length ? (
          <ChangeConfigurableForm
            idPrefix="edit"
            fields={editFields}
            form={form}
            extras={extras}
            onFormChange={handleFormChange}
            onExtraChange={(fieldId, value: ChangeExtraValue) =>
              setExtras((prev) => ({ ...prev, [String(fieldId)]: value }))
            }
            categories={categories}
            requesterAreas={requesterAreas}
            taskTypes={taskTypes}
            currentCategory={change.category}
            currentRequesterArea={change.requester_area}
            override={{ isAdmin, forceOutside, forceReason }}
            onOverrideChange={handleOverrideChange}
            showPeopleFields={false}
            showTaskTypes={false}
            typeOptions={typeOptions}
            dateRevisions={change.date_revisions}
          />
        ) : (
          <ChangeFormFields
            idPrefix="edit"
            form={form}
            onFormChange={handleFormChange}
            categories={categories}
            requesterAreas={requesterAreas}
            currentCategory={change.category}
            currentRequesterArea={change.requester_area}
            override={{ isAdmin, forceOutside, forceReason }}
            onOverrideChange={handleOverrideChange}
            dateRevisions={change.date_revisions}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default ChangeEditForm;
